import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, updateDoc, serverTimestamp, getDocs, doc, getDoc, runTransaction } from 'firebase/firestore';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../firebase';
import { submitCaseToEngine } from '../services/engineService';
import {
  UPLOAD_CATEGORIES, DEFAULT_ACCEPT,
  isCbctFile, filterCbctFiles,
} from '../config/uploadCategories';
import { smartSort, YELLOW_BELOW } from '../services/smartSort';
import { TOOTH_LIBRARIES } from '../config/toothLibraries';
import PhotoTemplate, { PHOTO_TEMPLATE_SLOTS } from '../components/PhotoTemplate';

const PREOP_PHOTO_TYPES = [
  'Frontal Smile',
  'Retracted Frontal',
  'Right Lateral Smile',
  'Left Lateral Smile',
  'Profile Smile',
  'Relaxed Lip',
  'Upper Occlusal',
  'Lower Occlusal',
  'Full Face Frontal',
  'Other',
];

// Photogrammetry systems and their proprietary scan markers (marker auto-fills from system)
const PHOTOGRAMMETRY_SYSTEMS = [
  { system: 'PIC / PICcamera (PIC dental)', marker: 'PIC transfer (PICabutment)' },
  { system: 'iCam4D (Imetric 4D)',          marker: 'iCamBody' },
  { system: 'MicronMapper (Claronav / S.I.N.)', marker: 'MicronMapper ScanBody' },
  { system: 'Aoralscan Elite (SHINING 3D)', marker: 'IPG Coded Scanbody' },
  { system: 'Other',                        marker: '' },
];

// Scan categories where arch identity matters (maxilla vs mandible). Tagging each
// file's arch fixes the wrong-arch file pick AND labels learning data from day one.
const ARCH_SCAN_CATEGORIES = new Set(['preop3d', 'surgical', 'photogrammetry', 'postop']);
const ARCH_OPTIONS = [
  { value: 'upper', label: 'Upper (Maxilla)' },
  { value: 'lower', label: 'Lower (Mandible)' },
];
// Stable key for a File across drag/remove reindexing (name + size is unique enough).
const fileSig = (f) => `${f.name}|${f.size}`;
// Derive the arch this case primarily processes from the selected case type.
const archFromCaseType = (caseType) => {
  if (!caseType) return null;
  if (caseType.includes('Upper')) return 'upper';
  if (caseType.includes('Lower')) return 'lower';
  return null; // Dual / Other → no single preference (engine processes upper first for now)
};

// Required-field markers temporarily removed — all fields optional for now.
const R = null;

// ─── Upload Modal ────────────────────────────────────────────────────────────
function UploadModal({ categoryFiles, setCategoryFiles, preop2dLabels, setPreop2dLabels, photoTemplate, setPhotoTemplate, fileArch, setFileArch, photoSystem, setPhotoSystem, onClose }) {
  const [unsorted, setUnsorted] = useState([]);
  const [bulkDragOver, setBulkDragOver] = useState(false);
  const [catDragOver, setCatDragOver] = useState(null);
  const [unsortedDragOver, setUnsortedDragOver] = useState(false);
  const [sorting, setSorting] = useState(false);
  const [sortSummary, setSortSummary] = useState(null); // {sorted, review, cats, unknown}
  const bulkInputRef = useRef();
  const fileMeta = useRef(new Map());  // File -> { confidence, reason } from the auto-sort

  // Internal drag tracking (which file is being dragged from where)
  const dragging = useRef(null); // { file, source: 'unsorted'|catKey, index }

  // ── Smart auto-sort: one zip (or a pile of files) → straight into the categories ──
  const autoSort = async (files) => {
    if (!files || !files.length) return;
    setSorting(true);
    try {
      const { assignments, unknown, summary } = await smartSort(files);
      setCategoryFiles(prev => {
        const next = { ...prev };
        for (const [key, arr] of Object.entries(assignments)) {
          const limit = key === 'preop2d' ? 12 : Infinity;
          const room = limit - (next[key]?.length || 0);
          const take = arr.slice(0, Math.max(0, room));
          take.forEach(a => fileMeta.current.set(a.file, { confidence: a.confidence, reason: a.reason }));
          next[key] = [...(next[key] || []), ...take.map(a => a.file)];
        }
        return next;
      });
      if (unknown.length) setUnsorted(prev => [...prev, ...unknown]);
      setSortSummary(summary);
    } catch (err) {
      // never block the doctor — fall back to the manual tray
      setUnsorted(prev => [...prev, ...Array.from(files)]);
    } finally {
      setSorting(false);
    }
  };

  const allPhotosLabeled = categoryFiles.preop2d.length === 0 ||
    categoryFiles.preop2d.every((_, i) => !!preop2dLabels[i]);
  // Scans are no longer required — readiness only nudges labeling uploaded photos.
  const requiredDone = allPhotosLabeled;
  const completedCount = UPLOAD_CATEGORIES.filter(c => categoryFiles[c.key].length > 0).length;
  const requiredCount = UPLOAD_CATEGORIES.length;

  // ── Bulk drop from filesystem → auto-sort ──
  const handleBulkDrop = (e) => {
    e.preventDefault();
    setBulkDragOver(false);
    autoSort(Array.from(e.dataTransfer.files));
  };

  const handleBulkBrowse = (e) => {
    autoSort(Array.from(e.target.files));
  };

  // ── Category drop (from filesystem OR from internal drag) ──
  const handleCatDrop = (e, key) => {
    e.preventDefault();
    setCatDragOver(null);

    // Internal drag (moving a file from unsorted or another category)
    if (dragging.current) {
      const { file, source, index } = dragging.current;
      dragging.current = null;

      // CBCT only accepts DICOM / zipped series — reject anything else (leave it in place)
      if (key === 'cbct' && !isCbctFile(file)) {
        filterCbctFiles([file]);
        return;
      }

      // Remove from source
      if (source === 'unsorted') {
        setUnsorted(prev => prev.filter((_, i) => i !== index));
      } else {
        setCategoryFiles(prev => ({
          ...prev,
          [source]: prev[source].filter((_, i) => i !== index)
        }));
      }

      // Add to target category (enforce 12-file cap for preop2d)
      setCategoryFiles(prev => {
        const limit = key === 'preop2d' ? 12 : Infinity;
        if (prev[key].length >= limit) return prev;
        return { ...prev, [key]: [...prev[key], file] };
      });
      return;
    }

    // Filesystem drop (enforce 12-file cap for preop2d, DICOM-only for cbct)
    let files = Array.from(e.dataTransfer.files);
    if (key === 'cbct') files = filterCbctFiles(files);
    if (files.length > 0) {
      setCategoryFiles(prev => {
        const limit = key === 'preop2d' ? 12 : Infinity;
        const slots = limit - prev[key].length;
        if (slots <= 0) return prev;
        return { ...prev, [key]: [...prev[key], ...files.slice(0, slots)] };
      });
    }
  };

  // ── Drop back to unsorted ──
  const handleUnsortedDrop = (e) => {
    e.preventDefault();
    setUnsortedDragOver(false);
    if (!dragging.current) return;
    const { file, source, index } = dragging.current;
    dragging.current = null;
    if (source === 'unsorted') return;
    setCategoryFiles(prev => ({
      ...prev,
      [source]: prev[source].filter((_, i) => i !== index)
    }));
    // Shift preop2d labels when a photo is dragged back to unsorted
    if (source === 'preop2d') {
      setPreop2dLabels(prev => {
        const next = {};
        Object.entries(prev).forEach(([k, v]) => {
          const ki = parseInt(k);
          if (ki < index) next[ki] = v;
          else if (ki > index) next[ki - 1] = v;
        });
        return next;
      });
    }
    setUnsorted(prev => [...prev, file]);
  };

  const removeFromCategory = (key, idx) => {
    setCategoryFiles(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
    if (key === 'preop2d') {
      setPreop2dLabels(prev => {
        const next = {};
        Object.entries(prev).forEach(([k, v]) => {
          const ki = parseInt(k);
          if (ki < idx) next[ki] = v;
          else if (ki > idx) next[ki - 1] = v;
        });
        return next;
      });
    }
  };

  const removeFromUnsorted = (idx) => {
    setUnsorted(prev => prev.filter((_, i) => i !== idx));
  };

  const totalFiles = Object.values(categoryFiles).flat().length + unsorted.length;

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
      zIndex:1000, display:'flex', alignItems:'flex-start', justifyContent:'center',
      overflowY:'auto', padding:'32px 16px'
    }}>
      <div style={{
        background:'white', borderRadius:'16px', width:'100%', maxWidth:'780px',
        boxShadow:'0 24px 60px rgba(0,0,0,0.2)', overflow:'hidden'
      }}>

        {/* Header */}
        <div style={{
          padding:'20px 28px', borderBottom:'1px solid #f0f0f0',
          display:'flex', alignItems:'center', justifyContent:'space-between'
        }}>
          <div>
            <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
              <button
                onClick={onClose}
                style={{
                  width:'32px', height:'32px', borderRadius:'50%',
                  border:'1px solid #e5e7eb', background:'white',
                  cursor:'pointer', fontSize:'16px', color:'#888',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  lineHeight:1, flexShrink:0
                }}
              >×</button>
              <div>
                <div style={{fontSize:'18px', fontWeight:'700', color:'#111'}}>Upload Case Files</div>
                <div style={{fontSize:'13px', color:'#888', marginTop:'2px'}}>
                  {completedCount} of {requiredCount} categories have files (all optional)
                </div>
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div style={{display:'flex', alignItems:'center', gap:'16px'}}>
            <div style={{width:'120px', height:'6px', background:'#f0f0f0', borderRadius:'3px'}}>
              <div style={{
                width:`${(completedCount/requiredCount)*100}%`, height:'6px',
                background: requiredDone ? '#22c55e' : '#c8922a',
                borderRadius:'3px', transition:'width 0.3s'
              }}/>
            </div>
            <button
              onClick={onClose}
              style={{
                padding:'8px 20px', borderRadius:'8px', border:'none',
                background: '#c8922a',
                color: 'white',
                fontWeight:'600', fontSize:'14px',
                cursor: 'pointer',
                transition:'all 0.3s'
              }}
            >
              {requiredDone ? 'Done ✓' : 'Done'}
            </button>
          </div>
        </div>

        <div style={{padding:'24px 28px', display:'flex', flexDirection:'column', gap:'20px'}}>

          {/* ── SMART DROP — one zip, auto-sorted ── */}
          <div
            onDragOver={e => { e.preventDefault(); setBulkDragOver(true); }}
            onDragLeave={() => setBulkDragOver(false)}
            onDrop={handleBulkDrop}
            onClick={() => bulkInputRef.current?.click()}
            style={{
              position:'relative', overflow:'hidden',
              border: bulkDragOver ? '2px solid #e0b84a' : '1px solid #262626',
              borderRadius:'16px', padding:'30px 28px', cursor:'pointer',
              background: bulkDragOver
                ? 'linear-gradient(135deg, #1c1507 0%, #0a0a0a 100%)'
                : 'linear-gradient(135deg, #161616 0%, #0a0a0a 100%)',
              boxShadow: bulkDragOver ? '0 0 0 4px rgba(200,146,42,0.18)' : '0 8px 24px rgba(0,0,0,0.18)',
              transition:'all 0.2s', textAlign:'center'
            }}
          >
            <div style={{position:'absolute', top:'-50px', left:'50%', transform:'translateX(-50%)',
              width:'260px', height:'130px', pointerEvents:'none',
              background:'radial-gradient(ellipse, rgba(200,146,42,0.28), transparent 70%)'}}/>
            <div style={{fontSize:'30px', marginBottom:'10px', position:'relative'}}>{sorting ? '⏳' : '🗂️'}</div>
            <div style={{fontSize:'17px', fontWeight:'700', color:'#fff', letterSpacing:'-0.01em', position:'relative'}}>
              {sorting ? 'Sorting your case…' : 'Drop your entire case here'}
            </div>
            <div style={{fontSize:'13px', color:'#e0b84a', marginTop:'6px', fontWeight:'600', position:'relative'}}>
              One zip — or all your files — ArchX sorts them into the right categories for you
            </div>
            <div style={{fontSize:'12px', color:'rgba(255,255,255,0.42)', marginTop:'8px', position:'relative'}}>
              .zip · .stl · .csv · .dcm · photos · .obj — drop or click to browse
            </div>
            <input
              ref={bulkInputRef}
              type="file" multiple
              accept={`${DEFAULT_ACCEPT},.dicom,.zip`}
              style={{display:'none'}}
              onChange={handleBulkBrowse}
            />
          </div>

          {/* ── Auto-sort result banner ── */}
          {sortSummary && (
            <div style={{
              display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap',
              padding:'12px 16px', borderRadius:'12px',
              background:'#f0fdf4', border:'1px solid #bbf7d0'
            }}>
              <span style={{fontSize:'18px'}}>✨</span>
              <span style={{fontSize:'13px', color:'#166534', fontWeight:'600'}}>
                Auto-sorted {sortSummary.sorted} file{sortSummary.sorted!==1?'s':''} into {sortSummary.cats} categor{sortSummary.cats!==1?'ies':'y'}
              </span>
              {sortSummary.review > 0 && (
                <span style={{fontSize:'12px', color:'#92400e', background:'#fef3c7', border:'1px solid #fde68a',
                  borderRadius:'20px', padding:'3px 10px', fontWeight:'600'}}>
                  ⚠ {sortSummary.review} to confirm
                </span>
              )}
              {sortSummary.unknown > 0 && (
                <span style={{fontSize:'12px', color:'#9a3412', background:'#ffedd5', border:'1px solid #fed7aa',
                  borderRadius:'20px', padding:'3px 10px', fontWeight:'600'}}>
                  {sortSummary.unknown} unrecognized → in the tray
                </span>
              )}
              <span style={{marginLeft:'auto', fontSize:'12px', color:'#16a34a'}}>Glance & confirm below ↓</span>
            </div>
          )}

          {/* Clinical 2D Photo Series — guided capture template */}
          <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
            <div style={{flex:1, height:'1px', background:'#eee'}}/>
            <span style={{fontSize:'11px', color:'#aaa', textTransform:'uppercase', letterSpacing:'0.05em'}}>or capture the clinical 2D photo series</span>
            <div style={{flex:1, height:'1px', background:'#eee'}}/>
          </div>
          <PhotoTemplate photos={photoTemplate} setPhotos={setPhotoTemplate} />

          {/* Sort area: unsorted tray (left column) beside the category drop zones (right) */}
          <div style={{display:'flex', gap:'16px', alignItems:'flex-start'}}>

          {/* Unsorted Tray — sticky left column for easy drag-and-drop into categories */}
          {unsorted.length > 0 && (
            <div
              onDragOver={e => { e.preventDefault(); setUnsortedDragOver(true); }}
              onDragLeave={() => setUnsortedDragOver(false)}
              onDrop={handleUnsortedDrop}
              style={{
                flex:'0 0 250px', alignSelf:'flex-start',
                position:'sticky', top:'8px', maxHeight:'72vh', overflowY:'auto',
                border: unsortedDragOver ? '2px dashed #c8922a' : '2px solid #fde68a',
                borderRadius:'10px', padding:'14px 16px',
                background: unsortedDragOver ? '#fffbf0' : '#fffef0'
              }}
            >
              <div style={{fontSize:'12px', fontWeight:'600', color:'#92400e', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.05em'}}>
                → Drag each file into a category
              </div>
              <div style={{display:'flex', flexDirection:'column', gap:'6px'}}>
                {unsorted.map((f, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => dragging.current = { file: f, source: 'unsorted', index: i }}
                    style={{
                      display:'flex', alignItems:'center', gap:'6px', maxWidth:'100%',
                      background:'white', border:'1px solid #fcd34d',
                      borderRadius:'10px', padding:'4px 10px',
                      fontSize:'12px', cursor:'grab'
                    }}
                  >
                    <span style={{color:'#444', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1, minWidth:0}} title={f.name}>{f.name}</span>
                    <span style={{color:'#bbb', flexShrink:0}}>{(f.size/1024/1024).toFixed(1)}MB</span>
                    <span
                      style={{color:'#aaa', cursor:'pointer', fontSize:'14px', flexShrink:0}}
                      onClick={() => removeFromUnsorted(i)}
                    >×</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Category Zones — right column */}
          <div style={{flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:'8px'}}>
            {UPLOAD_CATEGORIES.map(cat => {
              const files = categoryFiles[cat.key];
              const complete = files.length > 0;
              const needsReview = files.some(f => {
                const m = fileMeta.current.get(f);
                return m && m.confidence < YELLOW_BELOW;
              });
              const isDragging = catDragOver === cat.key;
              const catInputRef = React.createRef();

              return (
                <div
                  key={cat.key}
                  onDragOver={e => { e.preventDefault(); setCatDragOver(cat.key); }}
                  onDragLeave={() => setCatDragOver(null)}
                  onDrop={e => handleCatDrop(e, cat.key)}
                  style={{
                    border: isDragging ? '2px dashed #c8922a'
                      : needsReview ? '2px solid #f59e0b'
                      : complete ? '2px solid #22c55e' : '2px dashed #e5e7eb',
                    borderRadius:'10px', padding:'12px 16px',
                    background: isDragging ? '#fffbf0'
                      : needsReview ? '#fffbeb'
                      : complete ? '#f0fdf4' : '#fafafa',
                    transition:'all 0.2s'
                  }}
                >
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                    <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                      {/* Check circle */}
                      <div style={{
                        width:'22px', height:'22px', borderRadius:'50%',
                        background: needsReview ? '#f59e0b' : complete ? '#22c55e' : '#e5e7eb',
                        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
                      }}>
                        {complete && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <span style={{fontSize:'13px', fontWeight:'500', color:'#222'}}>{cat.label}</span>
                        {cat.required
                          ? <span style={{color:'red', marginLeft:'4px'}}>*</span>
                          : <span style={{fontSize:'11px', color:'#aaa', marginLeft:'8px'}}>optional</span>
                        }
                      </div>
                    </div>

                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      {needsReview && (
                        <span style={{fontSize:'10px', color:'#92400e', background:'#fef3c7',
                          border:'1px solid #fde68a', borderRadius:'12px', padding:'2px 8px', fontWeight:'600'}}>
                          review
                        </span>
                      )}
                      {files.length > 0 && (
                        <span style={{fontSize:'11px', color: needsReview ? '#b45309' : '#22c55e', fontWeight:'500'}}>
                          {files.length} file{files.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      <button
                        type="button"
                        style={{
                          fontSize:'12px', padding:'4px 12px',
                          border:'1px solid #ddd', borderRadius:'6px',
                          background:'white', cursor:'pointer', color:'#555',
                          whiteSpace:'nowrap'
                        }}
                        onClick={() => catInputRef.current?.click()}
                      >
                        + Browse
                      </button>
                      <input
                        ref={catInputRef}
                        type="file" multiple
                        accept={cat.accept || DEFAULT_ACCEPT}
                        style={{display:'none'}}
                        onChange={e => {
                          let picked = Array.from(e.target.files);
                          if (cat.key === 'cbct') picked = filterCbctFiles(picked);
                          setCategoryFiles(prev => {
                            const limit = cat.key === 'preop2d' ? 12 : Infinity;
                            const slots = limit - prev[cat.key].length;
                            if (slots <= 0) return prev;
                            const toAdd = picked.slice(0, slots);
                            return { ...prev, [cat.key]: [...prev[cat.key], ...toAdd] };
                          });
                        }}
                      />
                    </div>
                  </div>

                  {/* Drop hint */}
                  {files.length === 0 && cat.key !== 'preop2d' && (
                    <div style={{fontSize:'11px', color:'#bbb', marginTop:'6px', paddingLeft:'32px'}}>
                      {cat.hint || 'Drag files here from the unsorted tray above, or drop directly from your computer'}
                    </div>
                  )}

                  {/* Photogrammetry — system selector (marker auto-fills) */}
                  {cat.key === 'photogrammetry' && (
                    <div style={{marginTop:'8px', paddingLeft:'32px'}}>
                      <div style={{fontSize:'11px', color:'#666', marginBottom:'4px'}}>Photogrammetry system used</div>
                      <select
                        value={photoSystem || ''}
                        onChange={e => setPhotoSystem(e.target.value)}
                        style={{
                          fontSize:'12px', border:'1px solid #d1d5db', borderRadius:'6px',
                          padding:'6px 8px', color: photoSystem ? '#333' : '#9ca3af',
                          background:'white', minWidth:'280px', cursor:'pointer'
                        }}
                      >
                        <option value="" disabled>Select photogrammetry system</option>
                        {PHOTOGRAMMETRY_SYSTEMS.map(s => (
                          <option key={s.system} value={s.system}>{s.system}</option>
                        ))}
                      </select>
                      {photoSystem && (() => {
                        const found = PHOTOGRAMMETRY_SYSTEMS.find(s => s.system === photoSystem);
                        return found && found.marker ? (
                          <div style={{fontSize:'11px', color:'#16a34a', marginTop:'6px'}}>
                            Scan marker: <strong>{found.marker}</strong>
                          </div>
                        ) : (
                          <div style={{fontSize:'11px', color:'#888', marginTop:'6px'}}>
                            Scan marker: enter in case notes if applicable
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Pre-Op 2D Photos — required type labels */}
                  {cat.key === 'preop2d' && (
                    <div style={{marginTop:'8px', paddingLeft:'32px'}}>
                      {files.length === 0 ? (
                        <div style={{fontSize:'11px', color:'#bbb'}}>
                          Drag files here or drop directly from your computer — up to 12 photos accepted
                        </div>
                      ) : (
                        <>
                          <div style={{fontSize:'11px', fontWeight:'600', color: allPhotosLabeled ? '#16a34a' : '#dc2626', marginBottom:'8px'}}>
                            {allPhotosLabeled
                              ? `✓ All ${files.length} photo${files.length !== 1 ? 's' : ''} labeled`
                              : `Label required for each photo — select a type below`}
                          </div>
                          <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
                            {files.map((f, i) => (
                              <div
                                key={i}
                                draggable
                                onDragStart={() => dragging.current = { file: f, source: cat.key, index: i }}
                                style={{
                                  background:'white',
                                  border: preop2dLabels[i] ? '1px solid #d1fae5' : '1px solid #fca5a5',
                                  borderRadius:'8px', padding:'6px 10px',
                                  fontSize:'12px', cursor:'grab',
                                  display:'flex', flexDirection:'column', gap:'5px',
                                  minWidth:'160px', maxWidth:'220px'
                                }}
                              >
                                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:'6px'}}>
                                  <span style={{color:'#444', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:'11px'}}>{f.name}</span>
                                  <span
                                    style={{color:'#aaa', cursor:'pointer', fontSize:'14px', flexShrink:0}}
                                    onClick={() => removeFromCategory(cat.key, i)}
                                  >×</span>
                                </div>
                                <select
                                  value={preop2dLabels[i] || ''}
                                  onChange={e => setPreop2dLabels(prev => ({ ...prev, [i]: e.target.value }))}
                                  onClick={e => e.stopPropagation()}
                                  style={{
                                    fontSize:'11px',
                                    border: preop2dLabels[i] ? '1px solid #d1d5db' : '1px solid #fca5a5',
                                    borderRadius:'4px', padding:'3px 5px',
                                    color: preop2dLabels[i] ? '#333' : '#9ca3af',
                                    background:'white', width:'100%', cursor:'pointer'
                                  }}
                                >
                                  <option value="" disabled>Select type *</option>
                                  {PREOP_PHOTO_TYPES.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                          {files.length >= 12 && (
                            <div style={{fontSize:'11px', color:'#e67e22', marginTop:'6px'}}>
                              Maximum of 12 photos reached.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* File chips — all non-preop2d categories */}
                  {cat.key !== 'preop2d' && files.length > 0 && (
                    <div style={{marginTop:'8px', paddingLeft:'32px', display:'flex', flexWrap:'wrap', gap:'6px'}}>
                      {files.map((f, i) => {
                        const archCat = ARCH_SCAN_CATEGORIES.has(cat.key);
                        const sig = fileSig(f);
                        return (
                          <div
                            key={i}
                            draggable
                            onDragStart={() => dragging.current = { file: f, source: cat.key, index: i }}
                            style={{
                              display:'flex', alignItems:'center', gap:'6px',
                              background:'white',
                              border: archCat && !fileArch[sig] ? '1px solid #fcd34d' : '1px solid #d1fae5',
                              borderRadius:'20px', padding:'3px 10px',
                              fontSize:'12px', cursor:'grab'
                            }}
                          >
                            <span style={{color:'#444'}}>{f.name}</span>
                            <span style={{color:'#bbb'}}>{(f.size/1024/1024).toFixed(1)}MB</span>
                            {archCat && (
                              <select
                                value={fileArch[sig] || ''}
                                onClick={e => e.stopPropagation()}
                                onChange={e => setFileArch(prev => ({ ...prev, [sig]: e.target.value }))}
                                style={{
                                  fontSize:'11px',
                                  border: fileArch[sig] ? '1px solid #d1d5db' : '1px solid #fcd34d',
                                  borderRadius:'10px', padding:'1px 4px',
                                  color: fileArch[sig] ? '#333' : '#b45309',
                                  background: fileArch[sig] ? 'white' : '#fffbeb', cursor:'pointer'
                                }}
                              >
                                <option value="" disabled>Arch…</option>
                                {ARCH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            )}
                            <span
                              style={{color:'#aaa', cursor:'pointer', fontSize:'14px'}}
                              onClick={() => removeFromCategory(cat.key, i)}
                            >×</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>

          {/* Footer */}
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'8px', borderTop:'1px solid #f0f0f0'}}>
            <div style={{fontSize:'12px', color:'#999'}}>
              {totalFiles} total file{totalFiles !== 1 ? 's' : ''} · {unsorted.length > 0 ? `${unsorted.length} unsorted` : 'all sorted'}
            </div>
            <button
              onClick={onClose}
              style={{
                padding:'10px 28px', borderRadius:'8px', border:'none',
                background: requiredDone ? '#c8922a' : '#e5e7eb',
                color: requiredDone ? 'white' : '#aaa',
                fontWeight:'600', fontSize:'14px',
                cursor: requiredDone ? 'pointer' : 'not-allowed',
                transition:'all 0.3s'
              }}
            >
              {requiredDone ? 'Save & Close →' : 'Label each photo to continue'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── Main NewCase Component ──────────────────────────────────────────────────
export default function NewCase({ user, setIsUploading }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    patientFirst: '',
    patientLast: '',
    doctorLabName: '',
    contactEmail: '',
    contactPhone: '',
    caseType: '',
    implantBrand: '',
    implantBrandOther: '',
    abutmentType: '',
    toothLibrary: '',
    photogrammetrySystem: '',
    notes: ''
  });
  const [categoryFiles, setCategoryFiles] = useState(
    Object.fromEntries(UPLOAD_CATEGORIES.map(c => [c.key, []]))
  );
  const [preop2dLabels, setPreop2dLabels] = useState({});
  const [photoTemplate, setPhotoTemplate] = useState({});   // slotId -> File (clinical photo series)
  const [fileArch, setFileArch] = useState({});             // fileSig -> 'upper'|'lower' (arch tag per scan)
  const [photoSystem, setPhotoSystem] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [nextCaseNum, setNextCaseNum] = useState(null);

  // Show a PREVIEW of the next number. The firm, collision-proof number is
  // allocated atomically at submit (see allocateCaseNumber). Preview = max of the
  // global counter and (highest existing case number + 1).
  useEffect(() => {
    const previewNextCaseNumber = async () => {
      try {
        let maxNum = 0;
        const all = await getDocs(collection(db, 'cases'));
        all.forEach(d => {
          const m = /ARCHX-(\d+)/.exec(d.data().caseNumber || '');
          if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
        });
        let counterNext = 0;
        const c = await getDoc(doc(db, 'users', '_caseCounter'));
        if (c.exists()) counterNext = c.data().next || 0;
        const n = Math.max(maxNum + 1, counterNext);
        setNextCaseNum(`ARCHX-${String(n).padStart(5, '0')}`);
      } catch (e) {
        setNextCaseNum(null);
      }
    };
    previewNextCaseNumber();
  }, [user]);

  // Allocate a unique, never-reused case number via a Firestore transaction on a
  // global counter doc. Seeds the counter to (highest existing number + 1) so it
  // can never collide with pre-existing cases, then only ever increments —
  // deleting a case no longer rolls the number back (the ARCHX-00004 collision).
  const allocateCaseNumber = async () => {
    let maxNum = 0;
    try {
      const all = await getDocs(collection(db, 'cases'));
      all.forEach(d => {
        const m = /ARCHX-(\d+)/.exec(d.data().caseNumber || '');
        if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
      });
    } catch (e) { /* fall back to counter-only below */ }
    const counterRef = doc(db, 'users', '_caseCounter');
    const n = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      const current = snap.exists() ? (snap.data().next || 0) : 0;
      const start = Math.max(current, maxNum + 1);
      tx.set(counterRef, { next: start + 1 }, { merge: true });
      return start;
    });
    return `ARCHX-${String(n).padStart(5, '0')}`;
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const totalFileCount = Object.values(categoryFiles).flat().length + Object.keys(photoTemplate).length;
  // "Ready" is now just a visual cue (files present); submission is no longer gated.
  const requiredComplete = totalFileCount > 0;
  const completedCount = UPLOAD_CATEGORIES.filter(c => categoryFiles[c.key].length > 0).length;
  const requiredCount = UPLOAD_CATEGORIES.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Required gates removed for now — submission is allowed with any/no fields.
    setUploading(true);
    if (setIsUploading) setIsUploading(true);
    try {
      // Allocate the firm, collision-proof number now (not the preview).
      let caseNum;
      try {
        caseNum = await allocateCaseNumber();
      } catch (e) {
        caseNum = nextCaseNum || `ARCHX-${Date.now().toString().slice(-5)}`;
      }
      const uploadedFiles = [];
      const allFiles = [];

      UPLOAD_CATEGORIES.forEach(cat => {
        categoryFiles[cat.key].forEach((file, idx) => {
          allFiles.push({
            file,
            category: cat.key,
            categoryLabel: cat.label,
            photoType: cat.key === 'preop2d' ? (preop2dLabels[idx] || '') : undefined,
            arch: ARCH_SCAN_CATEGORIES.has(cat.key) ? (fileArch[fileSig(file)] || undefined) : undefined,
          });
        });
      });

      // Clinical Photo Series — each filled slot uploads as a preop2d photo with
      // its slot label as the photo type (no manual labeling needed).
      PHOTO_TEMPLATE_SLOTS.forEach(slot => {
        const file = photoTemplate[slot.id];
        if (file) {
          const ext = (file.name.match(/\.[^.]+$/) || [''])[0] || '.jpg';
          allFiles.push({
            file,
            category: 'preop2d',
            categoryLabel: 'Pre-Op 2D Photos',
            photoType: slot.label,
            uploadName: `${slot.id}${ext}`,   // stable, collision-free name per slot
          });
        }
      });

      for (let i = 0; i < allFiles.length; i++) {
        const { file, category, photoType, uploadName, arch } = allFiles[i];
        const storageName = uploadName || file.name;
        const storagePath = `cases/${user.uid}/${caseNum}/${category}/${storageName}`;
        const storageRef = ref(storage, storagePath);

        await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, file);
          task.on('state_changed',
            snap => {
              const fileProgress = snap.bytesTransferred / snap.totalBytes;
              const overall = ((i + fileProgress) / allFiles.length) * 100;
              setProgress(Math.round(overall));
            },
            reject,
            () => {
              uploadedFiles.push({
                name: storageName,
                path: storagePath,
                size: file.size,
                category,
                ...(photoType ? { photoType } : {}),
                ...(arch ? { arch } : {})
              });
              resolve();
            }
          );
        });
      }

      const caseRef = await addDoc(collection(db, 'cases'), {
        ...form,
        practiceId: user.uid,
        practiceEmail: user.email,
        caseNumber: caseNum,
        status: 'uploaded',
        engineStatus: 'pending',
        files: uploadedFiles,
        fileCount: uploadedFiles.length,
        photogrammetrySystem: photoSystem || null,
        photogrammetryMarker: (PHOTOGRAMMETRY_SYSTEMS.find(s => s.system === photoSystem) || {}).marker || null,
        createdAt: serverTimestamp()
      });

      // Hand off to the AI engine for processing
      try {
        const engineResponse = await submitCaseToEngine(caseNum, user.uid, uploadedFiles, form.toothLibrary, archFromCaseType(form.caseType));
        await updateDoc(caseRef, {
          engineJobId: engineResponse.job_id,
          engineStatus: 'processing',
          status: 'processing',
        });
      } catch (engineErr) {
        // Engine unavailable — case saved, will retry later
        console.warn('Engine submit failed (will retry):', engineErr.message);
        await updateDoc(caseRef, { engineStatus: 'queued', status: 'uploaded' });
      }

      if (setIsUploading) setIsUploading(false);
      navigate('/dashboard');
    } catch (err) {
      console.error('Upload error:', err);
      alert('Upload failed: ' + err.message);
      setUploading(false);
      if (setIsUploading) setIsUploading(false);
    }
  };

  return (
    <>
      {modalOpen && (
        <UploadModal
          categoryFiles={categoryFiles}
          setCategoryFiles={setCategoryFiles}
          preop2dLabels={preop2dLabels}
          setPreop2dLabels={setPreop2dLabels}
          photoTemplate={photoTemplate}
          setPhotoTemplate={setPhotoTemplate}
          fileArch={fileArch}
          setFileArch={setFileArch}
          photoSystem={photoSystem}
          setPhotoSystem={setPhotoSystem}
          onClose={() => setModalOpen(false)}
        />
      )}

      <nav className="navbar">
        <div className="nav-logo">
          <div><span className="nav-logo-text">ARCHX</span><span className="nav-logo-ai">AI</span></div>
          <span className="nav-tagline">FULL ARCH INTELLIGENCE</span>
        </div>
        <div className="nav-right">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>← Back to dashboard</button>
        </div>
      </nav>

      <div className="page">
        <div className="card">
          {nextCaseNum && (
            <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'4px'}}>
              <span style={{fontSize:'11px', color:'#999'}}>Case number:</span>
              <span style={{fontSize:'13px', fontWeight:'500', color:'#c8922a', letterSpacing:'0.06em'}}>{nextCaseNum}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} noValidate>

            {/* Patient Info */}
            <div className="form-section-title">Patient info</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">First name {R}</label>
                <input className="form-input" name="patientFirst" value={form.patientFirst} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="form-label">Last name {R}</label>
                <input className="form-input" name="patientLast" value={form.patientLast} onChange={handleChange} required />
              </div>
            </div>

            {/* Doctor / Lab Info */}
            <div className="form-section-title">Doctor / Lab info</div>
            <div className="form-grid-2">
              <div className="form-group" style={{gridColumn:'span 2'}}>
                <label className="form-label">Doctor / Lab name {R}</label>
                <input className="form-input" name="doctorLabName" value={form.doctorLabName} onChange={handleChange} placeholder="Dr. Smith or ABC Dental Lab" required />
              </div>
              <div className="form-group">
                <label className="form-label">Email {R}</label>
                <input className="form-input" type="email" name="contactEmail" value={form.contactEmail} onChange={handleChange} placeholder="email@example.com" required />
              </div>
              <div className="form-group">
                <label className="form-label">Phone number {R}</label>
                <input className="form-input" type="tel" name="contactPhone" value={form.contactPhone} onChange={handleChange} placeholder="(555) 000-0000" required />
              </div>
            </div>

            {/* Case Details */}
            <div className="form-section-title">Case details</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Case type {R}</label>
                <select className="form-select" name="caseType" value={form.caseType} onChange={handleChange} required>
                  <option value="" disabled>Select</option>
                  <option>All on X Full Arch - Upper</option>
                  <option>All on X Full Arch - Lower</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Implant brand {R}</label>
                <select className="form-select" name="implantBrand" value={form.implantBrand} onChange={handleChange} required>
                  <option value="" disabled>Select</option>
                  <option>BioHorizons</option>
                  <option>Camlog</option>
                  <option>Dentium</option>
                  <option>Dentsply Sirona</option>
                  <option>Hiossen</option>
                  <option>Implant Direct</option>
                  <option>Keystone</option>
                  <option>MegaGen</option>
                  <option>MIS Implants</option>
                  <option>Neodent</option>
                  <option>Nobel Biocare</option>
                  <option>Paltop</option>
                  <option>SIN360</option>
                  <option>Southern Implants</option>
                  <option>Straumann</option>
                  <option>Zimmer Biomet (ZimVie)</option>
                  <option>Other</option>
                </select>
                {form.implantBrand === 'Other' && (
                  <input
                    className="form-input"
                    name="implantBrandOther"
                    value={form.implantBrandOther}
                    onChange={handleChange}
                    placeholder="Enter implant brand name"
                    required
                    style={{ marginTop: '8px' }}
                  />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Abutment Type {R}</label>
                <select className="form-select" name="abutmentType" value={form.abutmentType} onChange={handleChange} required>
                  <option value="" disabled>Select</option>
                  <option>Multi Unit Universal 4.8mm</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Photogrammetry system {R}</label>
                <select className="form-select" name="photogrammetrySystem" value={form.photogrammetrySystem} onChange={handleChange} required>
                  <option value="" disabled>Select</option>
                  <option>iCam 4D</option>
                  <option>MicroMapper</option>
                  <option>PIC</option>
                  <option>Tupel</option>
                  <option>Shining 3D Elite</option>
                  <option>TruAbutment T-Marker</option>
                  <option>ScanBridge</option>
                  <option>OptiSplint</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tooth library (design)</label>
                <select className="form-select" name="toothLibrary" value={form.toothLibrary} onChange={handleChange}>
                  <option value="">Engine default</option>
                  {TOOTH_LIBRARIES.map(l => (
                    <option key={l.slug} value={l.slug}>{l.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* File Upload Summary / Button */}
            <div className="form-section-title">File uploads {R}</div>

            <div
              onClick={() => setModalOpen(true)}
              style={{
                borderRadius:'14px',
                padding:'22px 28px',
                background: requiredComplete
                  ? 'linear-gradient(135deg, #0a2a1a 0%, #166534 100%)'
                  : 'linear-gradient(135deg, #0d1f35 0%, #1E3A5F 100%)',
                cursor:'pointer',
                transition:'all 0.3s',
                display:'flex', alignItems:'center', justifyContent:'space-between',
                boxShadow: requiredComplete
                  ? '0 4px 20px rgba(34,197,94,0.25)'
                  : '0 4px 20px rgba(200,146,42,0.2)',
                border: requiredComplete ? '1px solid #22c55e' : '1px solid #c8922a'
              }}
            >
              <div style={{display:'flex', alignItems:'center', gap:'16px'}}>
                {/* Icon */}
                <div style={{
                  width:'48px', height:'48px', borderRadius:'12px',
                  background: requiredComplete ? 'rgba(34,197,94,0.2)' : 'rgba(200,146,42,0.2)',
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
                }}>
                  {requiredComplete ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13l4 4L19 7" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="#c8922a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <div>
                  {requiredComplete ? (
                    <>
                      <div style={{fontSize:'15px', fontWeight:'700', color:'#22c55e', letterSpacing:'0.02em'}}>
                        Files Uploaded
                      </div>
                      <div style={{fontSize:'12px', color:'rgba(255,255,255,0.5)', marginTop:'3px'}}>
                        {totalFileCount} files across {completedCount} categories — click to review or add more
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{fontSize:'15px', fontWeight:'700', color:'white', letterSpacing:'0.02em'}}>
                        Add Files &amp; Scans
                      </div>
                      <div style={{fontSize:'12px', color:'rgba(255,255,255,0.45)', marginTop:'3px'}}>
                        {completedCount > 0 ? `${completedCount} of ${requiredCount} categories have files` : `${requiredCount} optional categories · drag, drop, or browse`}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Progress pills */}
              <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'8px'}}>
                <div style={{display:'flex', gap:'4px'}}>
                  {UPLOAD_CATEGORIES.map(cat => (
                    <div key={cat.key} style={{
                      width:'8px', height:'8px', borderRadius:'50%',
                      background: categoryFiles[cat.key].length > 0 ? '#22c55e' : 'rgba(255,255,255,0.2)',
                      transition:'background 0.3s'
                    }}/>
                  ))}
                </div>
                <div style={{
                  padding:'6px 14px', borderRadius:'8px',
                  background: requiredComplete ? '#22c55e' : '#c8922a',
                  color:'white', fontSize:'12px', fontWeight:'700',
                  letterSpacing:'0.04em', textTransform:'uppercase'
                }}>
                  {requiredComplete ? 'Review →' : 'Upload →'}
                </div>
              </div>
            </div>

            {/* Clinical Notes */}
            <div className="form-section-title" style={{marginTop:'20px'}}>Clinical notes</div>
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea className="form-textarea" name="notes" value={form.notes} onChange={handleChange} placeholder="Implant positions, special instructions, etc." />
            </div>

            {/* Upload progress */}
            {uploading && (
              <div style={{marginBottom:'16px'}}>
                <div style={{fontSize:'13px', color:'#888', marginBottom:'6px'}}>Uploading {totalFileCount} files... {progress}%</div>
                <div style={{background:'#f0f0f0', borderRadius:'4px', height:'4px'}}>
                  <div style={{background:'#c8922a', height:'4px', borderRadius:'4px', width:`${progress}%`, transition:'width 0.3s'}}/>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-ghost" onClick={() => navigate('/dashboard')}>Cancel</button>
              <button
                type="submit"
                disabled={uploading}
                style={{
                  padding:'10px 24px', borderRadius:'8px', border:'none',
                  fontWeight:'600', fontSize:'14px',
                  cursor: !uploading ? 'pointer' : 'not-allowed',
                  background: !uploading ? '#c8922a' : '#e5e7eb',
                  color: !uploading ? 'white' : '#aaa',
                  transition:'all 0.3s'
                }}
              >
                {uploading ? `Uploading ${progress}%...` : 'Submit case →'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </>
  );
}
